/*
  Turns a YouTube or Spotify link into the URL of its official embedded
  player, or null when the link is not something those players can show.

  Kept in step with src/linkEmbed.js on the server — the server uses this to
  decide whether a link may be displayed as a player at all, the client to
  render the iframe.
*/

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const SPOTIFY_KINDS = ['track', 'album', 'playlist', 'episode', 'show', 'artist'];

const parse = (raw) => {
  try {
    return new URL(String(raw || '').trim());
  } catch (err) {
    return null;
  }
};

const youtubeEmbed = (url) => {
  const host = url.hostname.replace(/^(www|m|music)\./, '');
  let id = null;

  if (host === 'youtu.be') {
    id = url.pathname.split('/')[1];
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const parts = url.pathname.split('/');
    if (parts[1] === 'watch') id = url.searchParams.get('v');
    else if (['shorts', 'embed', 'live'].indexOf(parts[1]) !== -1) id = parts[2];

    const list = url.searchParams.get('list');
    if (!id && list && /^[A-Za-z0-9_-]+$/.test(list)) {
      return { provider: 'youtube', kind: 'playlist', src: 'https://www.youtube-nocookie.com/embed/videoseries?list=' + list };
    }
  } else {
    return null;
  }

  return id && YOUTUBE_ID.test(id)
    ? { provider: 'youtube', kind: 'video', src: 'https://www.youtube-nocookie.com/embed/' + id }
    : null;
};

const spotifyEmbed = (url) => {
  if (url.hostname !== 'open.spotify.com') return null;
  // Localised links carry a prefix: /intl-tr/track/<id>
  const parts = url.pathname.split('/').filter(Boolean).filter((part) => !/^intl-/.test(part));
  const kind = parts[0];
  const id = parts[1];
  if (SPOTIFY_KINDS.indexOf(kind) === -1 || !/^[A-Za-z0-9]+$/.test(id || '')) return null;
  return { provider: 'spotify', kind, src: 'https://open.spotify.com/embed/' + kind + '/' + id };
};

const getEmbed = (raw) => {
  const url = parse(raw);
  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) return null;
  return youtubeEmbed(url) || spotifyEmbed(url);
};

export default getEmbed;
